import React, { useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Editor } from 'primereact/editor';
import { classNames } from 'primereact/utils';
import { Tooltip } from 'primereact/tooltip';

import { UserDetailsContext, useUserDetailsContext } from 'context/userDetailsContext';
import { useSecureMessageContext } from 'modules/messages/SecureMessages/Context/SecureMessageContext';
import { handleDateTimeOffset } from 'utils/utility_functions/timezone';
import { openLinkInBlank, replaceLawftDate, sanitizeHtmlColorContent } from 'utils/utils';
import { generateFullName } from 'utils/generateFullNameUtils';
import { SentimentDot } from 'components/SentimentDot/SentimentDot';
import { updateMessageThread } from 'services/messageServices';
import { getTenantIdFromLS, getTenantSkFromLS } from 'utils/localStorageUtils';
import { useNavbarContext } from 'context/NavbarContext';
import { WebSocketConnectionContext } from 'context/WebSocketConnectionContext';
import { getSignedUrl } from 'modules/file_manager/services';
import ImageComponent from 'shared/ImageComponent';
import constants from 'constants/index';
import moment from 'moment';
import { cancelScheduledMessage } from 'services/messages/messageService';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';

function Conversations({ messageDetails, fetchCaseMessageDetails }) {
  const history = useHistory();
  const { addToast } = useToast();

  const userContext = useContext(UserDetailsContext);
  const { userDetails } = useUserDetailsContext();

  const { isFromClientPortal, isGlobalMessagesScreen } = useSecureMessageContext();
  const { getNotificationCounts, notificationData, downloadFile } = useNavbarContext();
  const websocketContext = useContext(WebSocketConnectionContext);

  const onClickAttachments = async (e, fileInfo) => {
    e.preventDefault();
    const link = await getSignedUrl(fileInfo);
    downloadFile(link, fileInfo.display_name);
  };

  // navigate to event or task module
  function taskAndEventModuleNavigation(e, item, type) {
    let route = isFromClientPortal ? '/client' : '';
    if (type === 'event') {
      route += '/calendar';
    } else {
      route += '/tasks';
    }
    if (item?.task_name || item?.event_name) {
      if (!item?.case_name && !item?.case_id) {
        item.not_linked_to_case = false;
      }
      let searchQuery = item?.sk?.split('#')?.[1] || '';
      if (type === 'event') searchQuery += `&date=${moment(item?.meeting_start_time).format('YYYY-MM-DD')}`;
      redirectToPage(e, route, item, searchQuery);
    }
  }

  const redirectToPage = (e, pathname, state, searchParam) => {
    e.preventDefault();
    if (!openLinkInBlank(pathname, state)) {
      history?.push({
        pathname,
        state,
        search: searchParam ? `?id=${searchParam}` : '',
      });
    }
  };

  const navigateToUser = (item) => {
    if (item?.group_id) {
      return `/settings/groups`;
    }
    if (!isFromClientPortal && item?.access_level) {
      let route = '/';
      if (item?.access_level === 'client') {
        route = item?.contact_id ? `/contacts/${item?.contact_id}` : '/contacts';
      } else {
        route = item?.firm_user_id ? `/settings/team-management/${item?.firm_user_id}` : '/settings/team-management';
      }
      return route;
    }
  };

  useEffect(() => {
    if (messageDetails && websocketContext?.secureMessageData?.secure_message_type === 'reply') {
      const tenantId = getTenantIdFromLS();
      const tenantSk = getTenantSkFromLS();
      let sendData = {};
      sendData.sk = messageDetails?.sk;
      sendData.is_new = false;
      sendData.case_id = messageDetails?.case_id;
      sendData.is_read = messageDetails?.is_read;
      sendData.old_is_new = true;
      sendData.case_name = messageDetails?.case_name;
      sendData.subject = messageDetails?.subject;
      sendData.accessible_list = messageDetails?.accessible_list;
      sendData.assign_to = messageDetails?.assign_to || [];
      sendData.resolution_status = messageDetails?.resolution_status || false;
      sendData.resolved_status = messageDetails?.resolved_status || false;
      updateMessageThread(tenantId, tenantSk, sendData, messageDetails?.is_lead ? true : false)
        .then(() => {
          // prevent unwanted notification caount api calls
          //Fetching notification counts
          // !isFromClientPortal && notificationData?.loadNotificationCount();
          // isFromClientPortal && getNotificationCounts(userContext?.firmDetails?.contact_id);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [websocketContext.response, websocketContext.secureMessageData]);

  const deleteScheduledMessage = (msg) => {
    const data = { action: 'cancel' };

    cancelScheduledMessage(msg?.pk, msg?.sk, data)
      .then(() => {
        addToast(
          false,
          toastConstant.toasterType.SUCCESS,
          toastConstant.api.SUCCESS,
          toastConstant.message.SECURE_MESSAGE_CANCEL_SCHEDULED_MESSAGE_SUCCESS
        );
        fetchCaseMessageDetails();
      })
      .catch((error) => {
        console.log(error);
        addToast(
          false,
          toastConstant.toasterType.ERROR,
          toastConstant.api.FAILED,
          toastConstant.message.SECURE_MESSAGE_CANCEL_SCHEDULED_MESSAGE_FAILED
        );
      });
  };

  useEffect(() => {
    if (messageDetails?.message?.length === 0) {
      if (isGlobalMessagesScreen) {
        history.push('/messages');
      } else if (!messageDetails?.is_lead) {
        history.push(messageDetails?.case_id ? `/cases/${messageDetails.case_id}/messages` : '/messages');
      } else if (messageDetails?.is_lead) {
        history.push(messageDetails?.case_id ? `/leads/${messageDetails.case_id}/messages` : '/messages');
      }
    }
  }, [messageDetails, history, isGlobalMessagesScreen]);

  return (
    <>
      {messageDetails?.message?.length ? (
        messageDetails?.message.map((msg, index) => (
          <div
            className={`row col-12 p-3 msg-reply-wrap mssg-reply-bgcolor ${
              userDetails?.appearance_preference === 'dark' ? 'msg-reply-li-color' : ''
            }`}
            key={index}
          >
            <div className="col-md-8 col-sm-6 col-12 d-flex">
              <div className="msg-img-size">
                <Link className="avatar-wrp d-flex text-decoration-none" to={() => navigateToUser(msg?.created_by_details)}>
                  <ImageComponent
                    filePath={msg?.created_by_user_image}
                    fileName={msg?.created_by_name?.first_name}
                    fileSize="medium.jpg"
                    avatarSize="medium"
                    className={isFromClientPortal ? 'pe-none' : ''}
                  />
                </Link>
              </div>
              <div className="w-100">
                <div className="row">
                  <div className="col-sm-12 view-message-editor">
                    <Link
                      className={`p-m-0 text-bold mb-0 ${classNames({
                        'redirection-link-no-text-transform': !isFromClientPortal && Boolean(generateFullName(msg?.created_by_details)),
                      })}`}
                      to={() => navigateToUser(msg?.created_by_details)}
                    >
                      {msg?.created_by_details?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                      {generateFullName(msg?.created_by_details)}
                    </Link>
                    <div
                      className="d-flex w-100"
                      style={{
                        wordBreak: 'break-word',
                        wordWrap: 'break-word',
                        alignItems: 'baseline',
                      }}
                    >
                      {msg?.message && (
                        <Editor
                          value={
                            msg?.message
                              ? sanitizeHtmlColorContent(replaceLawftDate(msg?.message, userContext?.userDetails?.timezone))
                              : ' '
                          }
                          readOnly
                          className="message-reply pe-2"
                        />
                      )}
                      {messageDetails?.is_review_request && !isFromClientPortal && (
                        <Tooltip
                          content="This link is tracked for executing linked workflows, and clicking it may lead to unexpected results"
                          position="right"
                          target=".message-reply"
                          showDelay={500}
                        />
                      )}
                      {msg?.sentiment_analysis?.sentiment && !isFromClientPortal && (
                        <SentimentDot tooltipPosition="top" type={msg?.sentiment_analysis?.sentiment} typeOnly />
                      )}
                    </div>
                  </div>
                  <div className="col-sm-12 my-2">
                    {(msg.attachment && msg.attachment.length) ||
                    msg.event_attachment ||
                    (msg.task_attachment && msg.task_attachment.length) ? (
                      <>
                        {msg.attachment && msg.attachment.length
                          ? msg.attachment.map((index) => {
                              return (
                                <div key={index} className="p-chip p-component pointer text-nowrap ps-0 pe-3">
                                  <p
                                    className="small pointer p-chip-text"
                                    onClick={(e) => {
                                      onClickAttachments(e, index);
                                    }}
                                  >
                                    <i className="fas fa-file-pdf"></i> {index.file_name}
                                  </p>
                                </div>
                              );
                            })
                          : null}

                        {msg.event_attachment && msg.event_attachment.length
                          ? msg.event_attachment.map((event, i) => {
                              return (
                                <div
                                  key={i}
                                  className="p-chip p-component text-break ps-0"
                                  style={{ marginRight: '0.5rem' }}
                                  onClick={(e) => {
                                    taskAndEventModuleNavigation(e, event, 'event');
                                  }}
                                >
                                  <p className="small p-chip-text">
                                    <i className="icon-calendar"></i> {event && event.event_name ? event.event_name : '-'}
                                  </p>
                                </div>
                              );
                            })
                          : null}

                        {msg.task_attachment && msg.task_attachment.length
                          ? msg.task_attachment.map((task, i) => {
                              return (
                                <div
                                  key={i}
                                  className="p-chip p-component text-break"
                                  style={{ marginRight: '0.5rem' }}
                                  onClick={(e) => {
                                    taskAndEventModuleNavigation(e, task, 'task');
                                  }}
                                >
                                  <p className="text-muted small mb-1 p-chip-text">
                                    <i className="fas fa-tasks"></i> {task && task?.task_name ? task?.task_name : '-'}
                                  </p>
                                </div>
                              );
                            })
                          : null}
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-12 view-message-editor text-end-sm">
              {msg?.scheduled_mapping ? (
                messageDetails?.scheduled_send_at && messageDetails?.scheduled_status === 'scheduled' ? (
                  <div className="d-flex align-items-center justify-content-end gap-2">
                    <i className="far fa-clock" style={{ color: 'var(--icon-red)' }}></i>
                    <p className="mb-0">
                      Scheduled to send:{' '}
                      {handleDateTimeOffset(
                        userContext?.userDetails?.timezone,
                        messageDetails?.scheduled_send_at,
                        constants.month_date_year_time_12_format
                      )}
                    </p>
                    <i className="icon-delete F-size18 cursor-pointer" onClick={() => deleteScheduledMessage(msg)}></i>
                  </div>
                ) : null
              ) : msg?.scheduled_send_at && msg?.scheduled_status === 'scheduled' ? (
                <div className="d-flex align-items-center justify-content-end gap-2">
                  <i className="far fa-clock" style={{ color: 'var(--icon-red)' }}></i>
                  <p className="mb-0">
                    Scheduled to send:{' '}
                    {handleDateTimeOffset(
                      userContext?.userDetails?.timezone,
                      msg?.scheduled_send_at,
                      constants.month_date_year_time_12_format
                    )}
                  </p>
                  <i className="icon-delete F-size18 cursor-pointer" onClick={() => deleteScheduledMessage(msg)}></i>
                </div>
              ) : (
                <>
                  <p className="p-m-0 mb-0">
                    {handleDateTimeOffset(userContext?.userDetails?.timezone, msg.time, constants.month_date_year_time_12_format)}
                  </p>
                </>
              )}
              <div className="d-flex align-items-center gap-2"></div>
              {!isFromClientPortal && (
                <>
                  {msg?.viewed?.length
                    ? msg?.viewed?.map((user, index) => {
                        user['access_level'] = 'client';
                        return (
                          <React.Fragment key={index}>
                            <Link
                              key={index}
                              className={`p-m-0 text-bold mb-0 ${classNames({
                                'redirection-link-no-text-transform': Boolean(user?.viewed_by),
                              })}`}
                              to={() => navigateToUser(user)}
                            >
                              {user?.viewed_by}
                            </Link>
                            {user.viewed_at ? (
                              <p className="p-m-0 text-muted mb-0">
                                {`Viewed: ${handleDateTimeOffset(
                                  userContext?.userDetails?.timezone,
                                  user.viewed_at,
                                  constants.month_date_year_time_12_format
                                )}
                                    `}
                              </p>
                            ) : null}
                          </React.Fragment>
                        );
                      })
                    : null}
                </>
              )}
            </div>
          </div>
        ))
      ) : (
        <div className="row border-bottom mt-2">
          <div className="col-sm-12">No Message Details Found</div>
        </div>
      )}
    </>
  );
}

export default Conversations;
